import { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@mui/material';
import { Moment } from 'moment';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import {
  selectUnidadesDeNegocioAssociadas,
  selectUnidadeSelecionada,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { GridItemModalProps } from '../../../common/components/cardsGrid/cardsModalGrid';
import MultiSelectStyled, { SimpleMultiSelectOption } from '../../../common/components/input/multiSelectStyled';
import { useListClientes } from '../../../common/hooks/useListClientes';
import QCXDatePicker from '../../../../shared-components/date-picker/QCXDatePicker';
import { generateAcompanhamentoChegadaReport } from './AcompanhamentoChegadaPage.helpers';

interface IUnidadeDeNegocio {
  id: number;
  pessoa: { nome: string; nomeResumido: string };
}

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '52vw',
    },
  },
});

const formatDate = (date: Moment | null) => (date ? date.format('YYYY-MM-DD') : null);

const periodoInvalido = (inicio: Moment | null, fim: Moment | null) => !!inicio && !!fim && inicio.isAfter(fim);

const AcompanhamentoChegadaPage = ({ title, isOpen, closeModal }: GridItemModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const businessUnits: IUnidadeDeNegocio[] = useSelector(selectUnidadesDeNegocioAssociadas);
  const [clientes] = useListClientes();

  const [selectedTenants, setSelectedTenants] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);
  const [aberturaInicio, setAberturaInicio] = useState<Moment | null>(null);
  const [aberturaFim, setAberturaFim] = useState<Moment | null>(null);
  const [chegadaInicio, setChegadaInicio] = useState<Moment | null>(null);
  const [chegadaFim, setChegadaFim] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false);

  const nomeUnidade = (bu: IUnidadeDeNegocio) => bu.pessoa.nomeResumido ?? bu.pessoa.nome;

  useEffect(() => {
    if (selectedTenants.length > 0 || !selectedUnit) return;

    const unidadePrincipal = businessUnits.find((bu) => Number(bu.id) === Number(selectedUnit));

    if (unidadePrincipal) {
      setSelectedTenants([{ id: unidadePrincipal.id.toString(), value: nomeUnidade(unidadePrincipal) }]);
    }
  }, [businessUnits, selectedUnit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTenantsChange = (_event: SyntheticEvent, value: SimpleMultiSelectOption[] | null) =>
    setSelectedTenants(value ?? []);

  const handleClientsChange = (_event: SyntheticEvent, value: SimpleMultiSelectOption[] | null) =>
    setSelectedClients(value ?? []);

  const handleSubmit = async () => {
    if (!token || !selectedUnit) return;

    if (selectedTenants.length === 0) {
      dispatch(setErrorFeedback({ message: 'Selecione pelo menos uma Unidade.' }));
      return;
    }

    if (periodoInvalido(aberturaInicio, aberturaFim) || periodoInvalido(chegadaInicio, chegadaFim)) {
      dispatch(setErrorFeedback({ message: 'A data inicial não pode ser posterior à data final.' }));
      return;
    }

    setLoading(true);
    keycloak.updateToken(300);

    try {
      const error = await generateAcompanhamentoChegadaReport(
        token,
        selectedUnit,
        selectedTenants.map((t) => t.id.toString()),
        selectedClients.map((c) => c.id.toString()),
        formatDate(aberturaInicio),
        formatDate(aberturaFim),
        formatDate(chegadaInicio),
        formatDate(chegadaFim)
      );

      if (error) {
        dispatch(setErrorFeedback({ message: error }));
      } else {
        dispatch(setSuccessFeedback({ message: 'Relatório gerado com sucesso!' }));
        closeModal();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth error={selectedTenants.length === 0}>
              <MultiSelectStyled
                disablePortal={false}
                controlledValues={selectedTenants}
                options={businessUnits.map((bu) => ({ id: bu.id.toString(), value: nomeUnidade(bu) }))}
                onChangeAction={handleTenantsChange}
                label="Unidade Operacional *"
              />
              {selectedTenants.length === 0 && (
                <Typography variant="body2" color="error">
                  Este campo é obrigatório
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <QCXDatePicker label="Data de Abertura (Inicial)" value={aberturaInicio} onChange={setAberturaInicio} />
          </Grid>

          <Grid item xs={6}>
            <QCXDatePicker label="Data de Abertura (Final)" value={aberturaFim} onChange={setAberturaFim} />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <MultiSelectStyled
                disablePortal={false}
                controlledValues={selectedClients}
                options={clientes.map((c) => ({
                  id: c.id ?? '',
                  value: `(${c.pessoa?.cnpj ?? ''}) ${c.pessoa?.nome ?? ''}`,
                }))}
                onChangeAction={handleClientsChange}
                label="Cliente"
              />
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <QCXDatePicker label="Data de Chegada (Inicial)" value={chegadaInicio} onChange={setChegadaInicio} />
          </Grid>

          <Grid item xs={6}>
            <QCXDatePicker label="Data de Chegada (Final)" value={chegadaFim} onChange={setChegadaFim} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" gridGap={8}>
          <Button variant="outlined" color="primary" onClick={closeModal} disabled={loading}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={loading || selectedTenants.length === 0}
          >
            {loading ? 'Gerando...' : 'Gerar Relatório'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AcompanhamentoChegadaPage;
